// ─── PART 10: CRYPTO + HESTON + SENTIMENT ────────────────────────────────────
// Dependencies: part1_core (C, s, fmt, pct, fmtK, fmtB, DataStatus, AnalystButton)
// Exports: CryptoTab, SentimentTab, hestonFitChain (utility for OptionsTab)
//
// CryptoTab data sources:
//   CoinGecko API      — prices, market cap, volume, 7d chart (free, no key)
//   Deribit API        — BTC + ETH options chains, IV surface (free, no key)
//   CoinGlass API      — funding rates, liquidations, OI (free tier)
//   Alternative.me     — Crypto Fear & Greed Index (free, no key)
//   Blockchain.com     — BTC mempool / network data (free)
//
// SentimentTab data sources:
//   Alternative.me     — Crypto Fear & Greed (free)
//   CNN Fear & Greed   — 7-factor market sentiment (RapidAPI free tier or scrape)
//   ⚠ AAII / NAAIM were listed here but never built — removed 2026-08-25.
//   FRED               — Already connected — used for put/call ratio history
//
// Heston surface fitting (hestonFitChain):
//   Pure JS/math — no backend required for basic fitting
//   Fits 5 Heston params to live options chain via least squares
//   Backend hook: swap fetchHestonSurface() URL to /api/heston/{ticker}
//   when backend is ready — zero other changes needed
//
// Backend hooks (clearly marked ── BACKEND HOOK ──):
//   All external fetch calls wrapped in backendFetch() utility
//   When BACKEND_URL is set in config, routes through backend
//   Otherwise falls back to direct API call
//   Migration path: set BACKEND_URL, backend proxies + caches + rate-limits
//
// Rough volatility note:
//   rBergomi calibration is server-side only (Monte Carlo, too slow for browser)
//   hestonFitChain() is the browser-compatible approximation
//   When backend is live, fetchRoughVolSurface() replaces it with real rBergomi
//   H parameter (roughness index) is exposed as a display field when backend present

import { useState, useEffect, useCallback, useMemo } from "react";
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine,
  ScatterChart, Scatter, ZAxis,
} from "recharts";
import { AlertCircle, RefreshCw, TrendingUp, TrendingDown } from "lucide-react";
import {
  C, s, font, fmt, fmtK, fmtB, pct,
  DataStatus, AnalystButton,
  FRED_BASE, backendFetch,
} from "./core";

// ─── BACKEND HOOK UTILITY ─────────────────────────────────────────────────────
// backendFetch() is imported from ./core — drop-in replacement for fetch() that
// routes through window.QUANT_SIGNAL_BACKEND when configured, else direct.
// All external API calls in this file use backendFetch() for clean migration.

// ─── HESTON MODEL — BROWSER-SIDE FITTING ──────────────────────────────────────
// Fits 5 Heston parameters to an observed IV surface using least squares.
// Parameters: v0 (initial var), kappa (mean reversion), theta (long-run var),
//             xi (vol of vol), rho (spot/vol correlation)
//
// Uses Carr-Madan characteristic function pricing (fast, closed-form).
// Accuracy: sufficient for IV surface visualization and divergence detection.
// For production calibration with rough vol, use backend rBergomi worker.

// ── Complex arithmetic (the Heston characteristic function is complex-valued) ──
const hAdd  = (a, b) => ({ re: a.re + b.re, im: a.im + b.im });
const hSub  = (a, b) => ({ re: a.re - b.re, im: a.im - b.im });
const hMul  = (a, b) => ({ re: a.re * b.re - a.im * b.im, im: a.re * b.im + a.im * b.re });
const hDiv  = (a, b) => { const d = b.re * b.re + b.im * b.im; return { re: (a.re * b.re + a.im * b.im) / d, im: (a.im * b.re - a.re * b.im) / d }; };
const hExp  = (a) => { const e = Math.exp(a.re); return { re: e * Math.cos(a.im), im: e * Math.sin(a.im) }; };
const hLog  = (a) => ({ re: 0.5 * Math.log(a.re * a.re + a.im * a.im), im: Math.atan2(a.im, a.re) });
const hSqrt = (a) => { const m = Math.hypot(a.re, a.im); const re = Math.sqrt((m + a.re) / 2); let im = Math.sqrt(Math.max(0, (m - a.re) / 2)); if (a.im < 0) im = -im; return { re, im }; };
const hScale = (a, s) => ({ re: a.re * s, im: a.im * s });

// Heston characteristic-function factor f_j (Gatheral "little trap"), j = 1 or 2.
// This is the correct COMPLEX formulation — the previous real-only version
// produced nonsensical prices (RMSE ~383 vol pts).
function hestonF(j, phi, S, K, T, r, v0, kappa, theta, sigma, rho) {
  const uj = j === 1 ? 0.5 : -0.5;
  const bj = j === 1 ? kappa - rho * sigma : kappa;
  const iphi = { re: 0, im: phi };
  const rsp = hScale(iphi, rho * sigma);                    // rho*sigma*i*phi
  const bMinus = hSub({ re: bj, im: 0 }, rsp);              // bj - rho*sigma*i*phi
  // d = sqrt((rho*sigma*i*phi - bj)^2 - sigma^2*(2*uj*i*phi - phi^2))
  const rsMinusB = hSub(rsp, { re: bj, im: 0 });
  const t1 = hMul(rsMinusB, rsMinusB);
  const t2 = hScale({ re: -phi * phi, im: 2 * uj * phi }, sigma * sigma);
  const d = hSqrt(hSub(t1, t2));
  const num = hSub(bMinus, d);                              // bj - rho*sigma*i*phi - d
  const g = hDiv(num, hAdd(bMinus, d));
  const edT = hExp(hScale(d, -T));                          // e^{-d*T}
  const one = { re: 1, im: 0 };
  const logArg = hDiv(hSub(one, hMul(g, edT)), hSub(one, g));
  const C = hAdd(
    hScale(iphi, r * T),
    hScale(hSub(hScale(num, T), hScale(hLog(logArg), 2)), (kappa * theta) / (sigma * sigma)),
  );
  const D = hMul(hScale(num, 1 / (sigma * sigma)), hDiv(hSub(one, edT), hSub(one, hMul(g, edT))));
  return hExp(hAdd(hAdd(C, hScale(D, v0)), hScale(iphi, Math.log(S))));
}

// P_j = 0.5 + (1/pi) * integral_0^inf Re[ e^{-i*phi*ln(K)} f_j / (i*phi) ] dphi
// Midpoint rule (avoids the phi=0 removable singularity).
function hestonP(j, S, K, T, r, v0, kappa, theta, sigma, rho) {
  const lnK = Math.log(K), UPPER = 100, N = 64, h = UPPER / N;
  let sum = 0;
  for (let n = 0; n < N; n++) {
    const phi = (n + 0.5) * h;
    const f = hestonF(j, phi, S, K, T, r, v0, kappa, theta, sigma, rho);
    const eMinus = { re: Math.cos(-phi * lnK), im: Math.sin(-phi * lnK) };
    sum += hDiv(hMul(eMinus, f), { re: 0, im: phi }).re * h;
  }
  return 0.5 + sum / Math.PI;
}

// Black-Scholes implied volatility from price (Newton-Raphson)
function bsIV(price, S, K, T, r, isCall) {
  if (price <= 0 || T <= 0) return null;
  const F = S * Math.exp(r * T);
  let sigma = 0.3; // initial guess
  for (let i = 0; i < 50; i++) {
    const d1  = (Math.log(F / K) + 0.5 * sigma ** 2 * T) / (sigma * Math.sqrt(T));
    const d2  = d1 - sigma * Math.sqrt(T);
    const nd1 = normalCDF(isCall ? d1 : -d1);
    const nd2 = normalCDF(isCall ? d2 : -d2);
    const bsP = Math.exp(-r * T) * (isCall ? (F * nd1 - K * nd2) : (K * (1 - nd2) - F * (1 - nd1)));
    const vega = S * Math.sqrt(T) * normalPDF(d1);
    if (Math.abs(vega) < 1e-10) break;
    const diff = bsP - price;
    sigma -= diff / vega;
    if (Math.abs(diff) < 1e-6) break;
    sigma = Math.max(0.001, Math.min(10, sigma));
  }
  return sigma;
}

// Heston call price via the semi-analytical probability integrals P1, P2
// (Heston 1993 / Gatheral). xi is the vol-of-vol (sigma).
function hestonCallPrice(S, K, T, r, v0, kappa, theta, xi, rho) {
  if (T <= 0) return Math.max(0, S - K);
  const P1 = hestonP(1, S, K, T, r, v0, kappa, theta, xi, rho);
  const P2 = hestonP(2, S, K, T, r, v0, kappa, theta, xi, rho);
  return Math.max(0, S * P1 - K * Math.exp(-r * T) * P2);
}

function normalCDF(x) {
  const a1 = 0.254829592, a2 = -0.284496736, a3 = 1.421413741;
  const a4 = -1.453152027, a5 = 1.061405429, p = 0.3275911;
  const sign = x < 0 ? -1 : 1;
  x = Math.abs(x) / Math.sqrt(2);
  const t = 1 / (1 + p * x);
  const y = 1 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);
  return 0.5 * (1 + sign * y);
}

function normalPDF(x) { return Math.exp(-0.5 * x * x) / Math.sqrt(2 * Math.PI); }

// ── Main Heston calibration function ──────────────────────────────────────────
// Input: options chain array [{strike, iv, T, isCall}]
// Output: {v0, kappa, theta, xi, rho, rmse, surface}
export function hestonFitChain(chain, spot, r = 0.05) {
  if (!chain || chain.length < 4 || !spot) return null;

  // Usable options: IV present (decimal, e.g. 0.15), positive maturity, sane moneyness.
  const usable = chain.filter((o) =>
    o.iv && o.iv > 0 && o.T > 0 && o.strike > spot * 0.7 && o.strike < spot * 1.3
  );
  if (usable.length < 4) return null;

  // Subsample to <=24 evenly-spaced strikes so calibration cost is bounded
  // regardless of chain size (24 points define the smile for 5 parameters).
  const byStrike = [...usable].sort((a, b) => a.strike - b.strike);
  const M = 24;
  let obs = byStrike;
  if (byStrike.length > M) {
    obs = [];
    const seen = new Set();
    for (let i = 0; i < M; i++) {
      const o = byStrike[Math.round((i * (byStrike.length - 1)) / (M - 1))];
      // Dedup by (strike, maturity) so same-strike points from different expiries
      // are BOTH kept — a single-expiry chain has one T so this is unchanged.
      const key = `${o.strike}:${o.T}`;
      if (!seen.has(key)) { seen.add(key); obs.push(o); }
    }
  }

  // ATM-anchored initial guess. v0 = ATM instantaneous variance. theta (long-run
  // variance) prefers a ~30-day ATM IV from the term structure when available — a
  // better long-run anchor than the front smile — else nudges just above ATM.
  const clampVar = (v) => Math.max(0.001, Math.min(0.36, v));
  const atm = obs.reduce((b, o) => (Math.abs(o.strike - spot) < Math.abs(b.strike - spot) ? o : b));
  const v0Init = clampVar(atm.iv * atm.iv);

  // theta init: ATM IV at the expiry nearest 30 DTE (score = maturity distance,
  // with a tiny moneyness tie-break so we land on the ATM strike of that expiry).
  const D30 = 30 / 365;
  let atm30 = null, best30 = Infinity;
  for (const o of usable) {
    if (!(o.iv > 0)) continue;
    const score = Math.abs(o.T - D30) + Math.abs(o.strike - spot) / spot * 1e-4;
    if (score < best30) { best30 = score; atm30 = o; }
  }
  const has30 = atm30 && Math.abs(atm30.T - D30) <= 15 / 365;   // an expiry within ~15d of 30 DTE
  const thetaInit = clampVar(has30 ? atm30.iv * atm30.iv : atm.iv * atm.iv * 1.2);

  // Bounds: v0/theta [0.001,0.36], kappa [0.1,10], xi [0.01,2], rho [-0.99,0] (equity leverage).
  // v0/theta are VARIANCES → the 0.36 cap = √0.36 = 60% long-run/instantaneous
  // vol, already conservative for equity indices. Higher values are optimizer
  // artifacts on flat surfaces (was 2.0 = √2 = 141% vol).
  const bounds = [[0.001, 0.36], [0.1, 10], [0.001, 0.36], [0.01, 2], [-0.99, 0]];

  // Objective: sum of squared IV errors — fit in IV space (model IV vs market IV,
  // both decimal). Fitting in price space would blow the vol-unit RMSE up.
  const objective = (p) => {
    const [v0, kappa, theta, xi, rho] = p;
    if (v0 <= 0 || kappa <= 0 || theta <= 0 || xi <= 0 || rho <= -1 || rho >= 0) return 1e10;
    let sse = 0;
    for (const o of obs) {
      try {
        const price   = hestonCallPrice(spot, o.strike, o.T, r, v0, kappa, theta, xi, rho);
        const modelIV = bsIV(price, spot, o.strike, o.T, r, true) || 0;
        sse += (modelIV - o.iv) ** 2;
      } catch { sse += 1; }
    }
    return sse;
  };

  // Bounded pattern search (Hooke-Jeeves): shrink a coordinate's step only when
  // neither direction improves. Converges reliably for the smooth Heston smile.
  let params = [v0Init, 2.0, thetaInit, 0.3, -0.5];
  let bestScore = objective(params);
  let step = bounds.map(([lo, hi]) => (hi - lo) * 0.15);
  const minStep = bounds.map(([lo, hi]) => (hi - lo) * 5e-4);
  const MAX_EVALS = 2200;
  let evals = 1;
  while (evals < MAX_EVALS && step.some((s, d) => s > minStep[d])) {
    let improved = false;
    for (let dim = 0; dim < 5; dim++) {
      for (const dir of [1, -1]) {
        const trial = [...params];
        trial[dim] = Math.max(bounds[dim][0], Math.min(bounds[dim][1], params[dim] + dir * step[dim]));
        const score = objective(trial);
        evals++;
        if (score < bestScore) { bestScore = score; params = trial; improved = true; break; }
      }
    }
    if (!improved) step = step.map((s) => s * 0.5);
  }

  const [v0, kappa, theta, xi, rho] = params;
  const rmse = Math.sqrt(bestScore / obs.length);

  // Fitted surface for DISPLAY — one row per strike at a SINGLE expiry so the
  // heat map / divergence table stay a single smile. For a single-expiry chain
  // that's the whole chain (unchanged). For a multi-expiry cross-sectional fit
  // it's the front (nearest) expiry's smile priced under the shared parameters.
  const Ts = [...new Set(usable.map((o) => o.T))];
  const frontT = Math.min(...Ts);
  const surfaceObs = Ts.length > 1 ? usable.filter((o) => o.T === frontT) : usable;

  // ⚠⚠ The expiry the DISPLAY surface belongs to, returned so no consumer ever has
  // to re-derive it. That derivation is not a one-liner: the selection above runs
  // over `usable`, which applies THIS function's own filters — iv > 0, T > 0, AND
  // the ±30% moneyness band — plus the <4-point bail above. Any copy of the rule
  // living outside this file drifts the moment one of those moves, and drifts
  // silently, because a mismatched surface still lines up on strike.
  // Value is whatever the caller attached as `exp`; consumers normalise the format.
  const expiration = surfaceObs.find((o) => o.exp)?.exp || null;
  const strikes = [...new Set(surfaceObs.map((o) => o.strike))].sort((a, b) => a - b);
  const surface = strikes.map((K) => {
    const src = surfaceObs.find((o) => o.strike === K);
    const T = src?.T || 0.25;
    const price = hestonCallPrice(spot, K, T, r, v0, kappa, theta, xi, rho);
    const modelIV = bsIV(price, spot, K, T, r, true);
    const obs_iv = src?.iv;
    return {
      strike:    K,
      moneyness: fmt((K / spot - 1) * 100, 1),
      modelIV:   modelIV ? fmt(modelIV * 100, 1) : null,
      marketIV:  obs_iv  ? fmt(obs_iv  * 100, 1) : null,
      divergence: modelIV && obs_iv ? fmt((obs_iv - modelIV) * 100, 2) : null,
    };
  });

  return { v0, kappa, theta, xi, rho, rmse, surface,
    expiration,                   // the expiry `surface` was priced at (may be null)
    expiryCount:     Ts.length,   // 1 = single-expiry; >1 = cross-sectional fit
    // Derived insights
    volOfVol:        fmt(xi * 100, 1),
    meanRevSpeed:    fmt(kappa, 2),
    longRunVol:      fmt(Math.sqrt(theta) * 100, 1),
    spotVolCorr:     fmt(rho, 3),
    fittingError:    fmt(rmse * 100, 2),
    // Backend hook note: when backend available, replace with rBergomi H parameter
    roughnessNote:   "H parameter available with backend (rBergomi calibration)",
  };
}

// ── IV heat map — 7-step violet gradient by normalized IV position ──
// Display only: colors a cell by where its IV sits in [minIV, maxIV] of the surface.
const HESTON_HEAT = [0.10, 0.20, 0.35, 0.50, 0.65, 0.80, 0.95];
function hestonHeat(iv, minIV, maxIV) {
  if (!Number.isFinite(iv) || maxIV <= minIV) return "rgba(157, 78, 221, 0.10)";
  const norm = Math.max(0, Math.min(1, (iv - minIV) / (maxIV - minIV)));
  return `rgba(157, 78, 221, ${HESTON_HEAT[Math.min(6, Math.floor(norm * 7))]})`;
}

// Terminal-styled tooltip for the divergence bar chart (dark bg, platinum text).
function DivergenceTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  const div = d.divVal;
  const signal = div > 2 ? "MARKET RICH — model underprices"
    : div < -2 ? "MODEL RICH — market underprices"
    : "FAIR VALUE — within 2 vp";
  const sigColor = div > 2 ? C.amber : div < -2 ? C.purple : C.textDim;
  return (
    <div style={{
      background: "rgba(11, 14, 20, 0.95)",
      border: "1px solid rgba(255, 255, 255, 0.12)",
      color: C.text, fontFamily: font, fontSize: 11,
      padding: "8px 12px", borderRadius: 0, lineHeight: 1.5,
    }}>
      <div>Strike: ${d.strike} ({d.moneyness}%)</div>
      <div>Market IV: {d.marketIV}%</div>
      <div>Model IV: {d.modelIV}%</div>
      <div>Divergence: {div > 0 ? "+" : ""}{fmt(div, 2)} vp</div>
      <div style={{ color: sigColor, marginTop: 2 }}>{signal}</div>
    </div>
  );
}

// ─── HESTON SURFACE PANEL (injected into OptionsTab) ─────────────────────────
// `expiration` labels the single-expiry `chain` prop — the expiry the caller has
// selected. It is only consulted on the single-expiry fallback path; the
// multi-expiry path reads each expiry's own date off multiChain.
export function HestonSurfacePanel({ chain, spot, multiChain, expiration, anthropicKey, backendUrl, onResult }) {
  const [fitting,  setFitting]  = useState(false);
  const [result,   setResult]   = useState(null);
  const [error,    setError]    = useState("");
  const [paramsOpen, setParamsOpen] = useState(true);
  const [isNarrow, setIsNarrow] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 640px)").matches
  );

  // Surface calibration result up to the parent (→ globalSignals.hestonSurface)
  useEffect(() => {
    if (result && onResult) onResult(result);
  }, [result]); // eslint-disable-line react-hooks/exhaustive-deps

  // Responsive: stack the regime summary + widen touch targets on narrow screens.
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    const on = (e) => setIsNarrow(e.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);

  const fit = async () => {
    setFitting(true);
    setError("");
    setResult(null);

    // ── BACKEND HOOK ──
    // When backend is live: fetch(`${backendUrl}/api/heston/${ticker}`)
    // returns {v0, kappa, theta, xi, rho, rmse, surface, H} (H = roughness index)
    // For now: browser-side fitting
    if (backendUrl) {
      try {
        const res  = await fetch(`${backendUrl}/api/heston`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ chain, spot }),
        });
        const data = await res.json();
        setResult(data);
        setFitting(false);
        return;
      } catch {}
    }

    // Browser-side fallback
    setTimeout(() => {
      try {
        // Multi-expiry (cross-sectional) obs when the multi-expiry chain is
        // available: every option carries its REAL maturity (T = DTE/365) and
        // mid_iv (already decimal), so hestonFitChain fits ONE parameter set
        // across the whole surface. Falls back to the selected single-expiry
        // chain (T ≈ 0.25) when multi-expiry data is absent or too sparse.
        let obs = [];
        if (multiChain && Array.isArray(multiChain.expiries)) {
          obs = multiChain.expiries.flatMap((e) => {
            const T = (e?.days_to_expiry || 0) / 365;
            if (!(T > 0)) return [];
            return (e.options || [])
              .filter((o) => o?.greeks && o.greeks.mid_iv > 0 && typeof o.strike === "number")
              .map((o) => ({
                strike: o.strike,
                iv:     o.greeks.mid_iv,      // already decimal
                T,
                // Carries this expiry's own date so hestonFitChain can report
                // which one its display surface ended up being priced at.
                exp:    e.expiration || null,
                isCall: o.option_type === "call",
              }));
          });
        }
        if (obs.length < 4) {
          // Single-expiry fallback — unchanged behavior.
          obs = chain
            .filter((o) => o.iv && parseFloat(o.iv) > 0)
            .map((o) => ({
              strike: o.strike,
              iv:     parseFloat(o.iv) / 100,
              T:      0.25, // approximate — improve with actual DTE when available
              exp:    expiration || null,   // the caller's selected expiry
              isCall: !o.itm,
            }));
        }
        const fitted = hestonFitChain(obs, spot);
        if (fitted) setResult(fitted);
        else setError("Insufficient chain data for Heston calibration. Need 4+ strikes with IV.");
      } catch (e) {
        setError(`Calibration error: ${e.message}`);
      }
      setFitting(false);
    }, 100);
  };

  // Heat-map / ATM inputs — computed inline per render (no state).
  const surface = result?.surface || [];
  const atmStrike = surface.length
    ? surface.reduce((b, s) => (Math.abs(s.strike - spot) < Math.abs(b.strike - spot) ? s : b)).strike
    : null;
  const atmRow = surface.find((s) => s.strike === atmStrike);
  const atmIV = parseFloat(atmRow?.marketIV);
  const rmseVal = parseFloat(result?.fittingError);

  // ±20% near-money surface drives ALL display (heat map, chart, table). The
  // full chain is still used for calibration — this only filters what's shown,
  // keeping deep-ITM strikes (300%+ IV) from compressing the meaningful data.
  const nearSurface = surface.filter((sf) => spot > 0 && Math.abs(sf.strike - spot) / spot <= 0.20);
  const nearIVs = nearSurface
    .flatMap((sf) => [parseFloat(sf.modelIV), parseFloat(sf.marketIV)])
    .filter((v) => Number.isFinite(v));
  const nearMinIV = nearIVs.length ? Math.min(...nearIVs) : 0;
  const nearMaxIV = nearIVs.length ? Math.max(...nearIVs) : 1;
  const nearDivergences = nearSurface.filter((sf) => Math.abs(parseFloat(sf.divergence)) > 2);
  const topDiv = nearDivergences.length
    ? nearDivergences.reduce((b, sf) => (Math.abs(parseFloat(sf.divergence)) > Math.abs(parseFloat(b.divergence)) ? sf : b))
    : null;

  // Divergence-profile bar chart + fit-summary inputs.
  const nearDivVals = nearSurface.map((sf) => parseFloat(sf.divergence)).filter((v) => Number.isFinite(v));
  const maxAbsDiv = nearDivVals.length ? Math.max(...nearDivVals.map((v) => Math.abs(v))) : 0;
  const avgAbsDiv = nearDivVals.length ? nearDivVals.reduce((a, v) => a + Math.abs(v), 0) / nearDivVals.length : 0;
  const divAxisMax = Math.max(5, Math.ceil(maxAbsDiv));
  const divBarData = nearSurface.map((sf) => ({
    moneyness: sf.moneyness, strike: sf.strike, marketIV: sf.marketIV, modelIV: sf.modelIV,
    divVal: parseFloat(sf.divergence) || 0,
  }));
  const fitBarColor = rmseVal < 3 ? C.green : rmseVal < 8 ? "rgba(34,197,94,0.7)" : rmseVal < 15 ? C.amber : C.red;

  // Regime-summary labels.
  const fitLabel = rmseVal < 3 ? "EXCELLENT" : rmseVal < 8 ? "GOOD" : rmseVal < 15 ? "MODERATE" : "POOR";
  const fitColor = rmseVal < 8 ? C.green : rmseVal < 15 ? C.amber : C.red;
  const rhoV = parseFloat(result?.spotVolCorr);
  const rhoLabel = rhoV < -0.5 ? "Strong leverage effect" : rhoV > -0.3 ? "Weak leverage — unusual" : "Moderate leverage";
  const rhoColor = rhoV < -0.5 ? C.green : rhoV > -0.3 ? C.amber : C.textDim;

  return (
    <div style={s.panel}>
      <div style={s.panelH}>
        <span style={s.panelT}>HESTON VOL SURFACE</span>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {backendUrl
            ? <span style={{ ...s.tag(C.teal), fontSize: 11 }}>BACKEND · rBergomi available</span>
            : <span style={{ ...s.tag(C.amber), fontSize: 11 }}>BROWSER · rBergomi requires backend</span>
          }
          <button style={s.btn("teal")} onClick={fit} disabled={fitting || !chain?.length}>
            {fitting ? "CALIBRATING..." : "FIT HESTON MODEL"}
          </button>
        </div>
      </div>
      <div style={s.panelB}>
        <div style={{ fontSize: 13, color: C.textDim, marginBottom: 8, lineHeight: 1.65 }}>
          Fits 5 Heston stochastic volatility parameters to the live options chain.
          Divergences between model IV and market IV flag potentially mispriced options.
          Black-Scholes assumes constant vol — Heston allows vol to mean-revert and correlate with spot.
          {backendUrl && " rBergomi (rough vol) calibration available — includes Hurst roughness index."}
        </div>

        {error && <div style={{ color: C.red, fontSize: 13, marginBottom: 8 }}>{error}</div>}

        {result && (
          <>
            {/* Display note */}
            <div style={{ fontSize: 9, color: C.textDim, marginBottom: 12 }}>
              Displaying ±20% strikes · Full chain used for calibration
              {result.expiryCount > 1 && ` · cross-sectional fit across ${result.expiryCount} expiries`}
            </div>

            {/* SECTION 1 — REGIME SUMMARY (glance read) */}
            <div style={{ display: "flex", flexDirection: isNarrow ? "column" : "row", gap: 10, marginBottom: 14 }}>
              <div style={{ flex: 1, border: `1px solid ${C.border}`, borderRadius: 3, padding: "10px 14px", background: "rgba(255,255,255,0.02)" }}>
                <div style={{ fontSize: 9, color: C.textDim, letterSpacing: "0.1em", marginBottom: 4 }}>ATM IMPLIED VOL</div>
                <div style={{ fontSize: 28, fontWeight: 700, lineHeight: 1, color: Number.isFinite(atmIV) ? (atmIV < 15 ? C.green : atmIV < 25 ? C.amber : C.red) : C.textDim }}>
                  {Number.isFinite(atmIV) ? `${atmRow.marketIV}%` : "—"}
                </div>
                <div style={{ fontSize: 10, color: C.textDim, marginTop: 5 }}>at ${atmStrike ?? "—"} strike</div>
              </div>
              <div style={{ flex: 1, border: `1px solid ${C.border}`, borderRadius: 3, padding: "10px 14px", background: "rgba(255,255,255,0.02)" }}>
                <div style={{ fontSize: 9, color: C.textDim, letterSpacing: "0.1em", marginBottom: 4 }}>HESTON FIT QUALITY</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: Number.isFinite(rmseVal) ? fitColor : C.textDim }}>
                  {Number.isFinite(rmseVal) ? fitLabel : "—"}
                </div>
                <div style={{ fontSize: 10, color: C.textDim, marginTop: 5 }}>RMSE {result.fittingError} vol pts</div>
              </div>
              <div style={{ flex: 1, border: `1px solid ${C.border}`, borderRadius: 3, padding: "10px 14px", background: "rgba(255,255,255,0.02)" }}>
                <div style={{ fontSize: 9, color: C.textDim, letterSpacing: "0.1em", marginBottom: 4 }}>DEALER CORRELATION (ρ)</div>
                <div style={{ fontSize: 28, fontWeight: 700, lineHeight: 1, color: C.purple }}>{result.spotVolCorr}</div>
                <div style={{ fontSize: 10, color: rhoColor, marginTop: 5 }}>{rhoLabel}</div>
              </div>
            </div>

            {/* SECTION 2 — CALIBRATED PARAMETERS (collapsible) */}
            <div style={{ marginBottom: 14 }}>
              <div onClick={() => setParamsOpen((o) => !o)} style={{ fontSize: 10, color: C.textDim, letterSpacing: "0.1em", cursor: "pointer", userSelect: "none", marginBottom: paramsOpen ? 6 : 0 }}>
                CALIBRATED PARAMETERS {paramsOpen ? "▾" : "▸"}
              </div>
              {paramsOpen && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {[
                    { label: "v₀ (Initial Vol)",   value: `${fmt(Math.sqrt(result.v0) * 100, 1)}%` },
                    { label: "κ (Mean Reversion)", value: fmt(result.kappa, 2) },
                    { label: "θ (Long-Run Vol)",   value: `${result.longRunVol}%` },
                    { label: "ξ (Vol of Vol)",     value: fmt(result.xi, 2) },
                    { label: "ρ (Spot/Vol Corr)",  value: fmt(result.rho, 3) },
                  ].map((p) => (
                    <div key={p.label} style={{ flex: `1 1 ${isNarrow ? "42%" : "110px"}`, minWidth: 90, background: "rgba(255,255,255,0.03)", border: `1px solid ${C.border}`, borderRadius: 2, padding: "8px 12px" }}>
                      <div style={{ fontSize: 9, color: C.textDim, marginBottom: 2 }}>{p.label}</div>
                      <div style={{ fontSize: 16, fontWeight: 600, color: C.text }}>{p.value}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* SECTION 3 — IV HEAT MAP (2D grid — wraps to rows, no horizontal scroll) */}
            {nearSurface.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 10, color: C.textDim, letterSpacing: "0.08em", marginBottom: 6 }}>
                  VOLATILITY SURFACE — ±20% STRIKES
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(56px, 1fr))", gap: 3 }}>
                  {nearSurface.map((sf) => {
                    const iv  = parseFloat(sf.marketIV);
                    const div = parseFloat(sf.divergence);
                    const isATM = sf.strike === atmStrike;
                    const mispriced = Math.abs(div) > 2;
                    const m = parseFloat(sf.moneyness);
                    const divColor = !Number.isFinite(div) ? C.textDim : Math.abs(div) < 1 ? C.textDim : div > 0 ? C.amber : C.purple;
                    const cell = {
                      minWidth: 56, minHeight: 48, boxSizing: "border-box",
                      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                      background: hestonHeat(iv, nearMinIV, nearMaxIV),
                      border: isATM ? `2px solid ${C.amber}` : `1px solid ${C.border}`,
                      borderRadius: 2, padding: "3px 2px", overflow: "hidden", fontFamily: font,
                    };
                    if (mispriced) { cell.outline = "1px solid rgba(255, 82, 82, 0.6)"; cell.outlineOffset = "-1px"; }
                    return (
                      <div key={sf.strike} style={cell}>
                        {isATM && <div style={{ fontSize: 8, fontWeight: 700, color: C.amber, lineHeight: 1.1 }}>SPOT</div>}
                        <div style={{ fontSize: 8, color: C.textDim }}>{m >= 0 ? "+" : ""}{sf.moneyness}%</div>
                        <div style={{ fontSize: 11, color: C.text }}>{Number.isFinite(iv) ? `${sf.marketIV}%` : "—"}</div>
                        <div style={{ fontSize: 9, color: divColor }}>{Number.isFinite(div) ? `${div > 0 ? "+" : ""}${sf.divergence}` : "—"}</div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ fontSize: 9, color: C.textDim, marginTop: 4 }}>
                  Violet intensity = market IV level · amber = ATM/spot · red outline = mispriced (&gt;2 vp) · bottom = divergence (market − model, amber rich / violet model-rich)
                </div>
              </div>
            )}

            {/* SECTION 4 — DIVERGENCE PROFILE (replaces the model-vs-market lines) */}
            {nearSurface.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 10, color: C.textDim, letterSpacing: "0.12em", marginBottom: 2 }}>
                  DIVERGENCE PROFILE — MARKET vs MODEL
                </div>
                <div style={{ fontSize: 9, color: C.textDim, opacity: 0.7, marginBottom: 6, lineHeight: 1.5 }}>
                  Amber ↑ = market rich (buy model, sell market) · Violet ↓ = model rich (buy market, sell model)
                </div>
                <ResponsiveContainer width="100%" height={120}>
                  <BarChart data={divBarData} margin={{ top: 6, right: 8, left: 0, bottom: 2 }}>
                    <XAxis dataKey="moneyness" tick={{ fontSize: 9, fill: C.textDim }} tickLine={false} interval={4} tickFormatter={(v) => `${v}%`} />
                    <YAxis domain={[-divAxisMax, divAxisMax]} ticks={[-divAxisMax, 0, divAxisMax]} tick={{ fontSize: 9, fill: C.textDim }} tickLine={false} axisLine={false} width={42} tickFormatter={(v) => `${v} vp`} />
                    <Tooltip cursor={{ fill: "rgba(255,255,255,0.05)" }} content={<DivergenceTooltip />} />
                    <ReferenceLine y={0} stroke={C.border} />
                    {atmRow && (
                      <ReferenceLine x={atmRow.moneyness} stroke={C.amber} strokeDasharray="4 3" strokeOpacity={0.7}
                        label={{ value: "ATM", position: "top", fill: C.amber, fontSize: 9 }} />
                    )}
                    <Bar dataKey="divVal" isAnimationActive={false} shape={(props) => {
                      const { x, y, width, height, value } = props;
                      const nearZero = Math.abs(value) < 1;
                      const fill = nearZero
                        ? "rgba(74, 90, 106, 0.3)"          // C.textDim — strike exists, fair
                        : value > 0 ? "rgba(251, 191, 36, 0.8)"   // amber — market rich
                        : "rgba(157, 78, 221, 0.8)";              // violet — model rich
                      const h  = Math.max(nearZero ? 2 : 3, Math.abs(height));
                      const ty = value >= 0 ? y : y + height;
                      return <rect x={x} y={ty} width={width} height={h} fill={fill} rx={1} />;
                    }} />
                  </BarChart>
                </ResponsiveContainer>

                {/* FIT SUMMARY BAR */}
                <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 9, color: C.textDim, letterSpacing: "0.08em" }}>OVERALL FIT</span>
                  <div style={{ width: 200, height: 6, background: C.border, borderRadius: 3, overflow: "hidden" }}>
                    <div style={{ width: `${Number.isFinite(rmseVal) ? Math.min(100, (rmseVal / 20) * 100) : 0}%`, height: "100%", background: fitBarColor }} />
                  </div>
                  <span style={{ fontSize: 10, fontWeight: 600, color: fitBarColor }}>
                    {result.fittingError} vol pts — {fitLabel}
                  </span>
                </div>
                <div style={{ fontSize: 9, color: C.textDim, marginTop: 4 }}>
                  Avg divergence: {fmt(avgAbsDiv, 1)} vp · Max: {fmt(maxAbsDiv, 1)} vp
                  {topDiv ? ` at $${topDiv.strike} (${parseFloat(topDiv.divergence) > 0 ? "market rich" : "model rich"})` : ""}
                </div>
              </div>
            )}

            {/* SECTION 5 — MISPRICING TABLE */}
            {nearDivergences.length > 0 && (
              <div style={{ marginBottom: 4 }}>
                <div style={{ fontSize: 10, color: C.amber, fontWeight: 700, marginBottom: 3, letterSpacing: "0.06em" }}>
                  {nearDivergences.length} STRIKE{nearDivergences.length > 1 ? "S" : ""} WHERE MARKET DEVIATES FROM MODEL BY &gt;2 VOL PTS
                </div>
                {topDiv && (
                  <div style={{ fontSize: 10, color: C.textDim, marginBottom: 6 }}>
                    Largest divergence: ${topDiv.strike} at {parseFloat(topDiv.divergence) > 0 ? "+" : ""}{topDiv.divergence} vpts — {parseFloat(topDiv.divergence) > 0 ? "MARKET RICH" : "MODEL RICH"}
                  </div>
                )}
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                      {["STRIKE", "MONEYNESS", "MODEL IV", "MARKET IV", "DIVERGENCE", "SIGNAL"].map((h) => (
                        <th key={h} style={{ padding: "3px 6px", textAlign: "right", fontSize: 11, color: C.textDim, fontWeight: 700 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {nearDivergences.map((d, i) => {
                      const div = parseFloat(d.divergence);
                      const rich = div > 0; // market IV > model = market rich. market IV < model = cheap
                      return (
                        <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                          <td style={{ padding: "4px 6px", textAlign: "right", fontWeight: 700 }}>${d.strike}</td>
                          <td style={{ padding: "4px 6px", textAlign: "right", color: C.textDim }}>{d.moneyness}%</td>
                          <td style={{ padding: "4px 6px", textAlign: "right", color: C.teal }}>{d.modelIV}%</td>
                          <td style={{ padding: "4px 6px", textAlign: "right", color: C.amber }}>{d.marketIV}%</td>
                          <td style={{ padding: "4px 6px", textAlign: "right", fontWeight: 700, color: Math.abs(div) > 5 ? C.red : C.amber }}>
                            {div > 0 ? "+" : ""}{d.divergence} vpts
                          </td>
                          <td style={{ padding: "4px 6px", textAlign: "right" }}>
                            <span style={s.tag(rich ? C.red : C.green)}>
                              {rich ? "MARKET RICH" : "MARKET CHEAP"}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* rBergomi rough-vol is rendered by RBergomiPanel in OptionsTab
                (live term-structure calibration) — no static placeholder here. */}

            {(anthropicKey || window.QUANT_SIGNAL_BACKEND) && (
              <div style={{ marginTop: 12 }}>
                <AnalystButton
                  anthropicKey={anthropicKey}
                  panel="heston"
                  label="SYNTHESIZE"
                  context={
                    `Heston stochastic volatility model calibration results:\n` +
                    `Spot: $${fmt(spot)}\n` +
                    `Parameters: v0=${fmt(result.v0, 4)} (initial var), ` +
                    `kappa=${result.meanRevSpeed} (mean reversion), ` +
                    `theta=${fmt(result.theta || 0, 4)} (long-run var = ${result.longRunVol}% vol), ` +
                    `xi=${fmt(result.xi || 0, 3)} (vol of vol = ${result.volOfVol}%), ` +
                    `rho=${result.spotVolCorr} (spot/vol correlation)\n` +
                    `Fitting RMSE: ${result.fittingError} vol points (${fitLabel})\n` +
                    `Mispricing candidates within ±20% (divergence >2 vpts): ${nearDivergences.length}\n` +
                    (nearDivergences.length > 0 ? nearDivergences.map((d) =>
                      `  $${d.strike} (${d.moneyness}% moneyness): model ${d.modelIV}% vs market ${d.marketIV}% = ${d.divergence} vpts`
                    ).join("\n") : "  None") +
                    `\n\nAnalyze: what the fitted parameters imply about the current vol regime, ` +
                    `specifically the mean reversion speed and long-run vol vs current vol, ` +
                    `what the rho value tells us about the leverage effect strength, ` +
                    `and what the mispricing candidates suggest in terms of relative value. ` +
                    `Note any parameters that look unusual vs typical equity calibrations ` +
                    `(typical equity: kappa 2-5, theta 3-5% vol, xi 20-50%, rho -0.5 to -0.8).`
                  }
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// CRYPTO TAB
// ═══════════════════════════════════════════════════════════════════════════════

const CRYPTO_ASSETS = [
  { id: "bitcoin",        symbol: "BTC",  name: "Bitcoin"  },
  { id: "ethereum",       symbol: "ETH",  name: "Ethereum" },
  { id: "solana",         symbol: "SOL",  name: "Solana"   },
  { id: "binancecoin",    symbol: "BNB",  name: "BNB"      },
  { id: "ripple",         symbol: "XRP",  name: "XRP"      },
  { id: "cardano",        symbol: "ADA",  name: "Cardano"  },
  { id: "avalanche-2",    symbol: "AVAX", name: "Avalanche"},
  { id: "chainlink",      symbol: "LINK", name: "Chainlink"},
];

// Deribit instruments for options
const DERIBIT_INSTRUMENTS = ["BTC", "ETH"];

export function CryptoTab({ keys, onData }) {
  const [prices,      setPrices]      = useState({});
  const [fearGreed,   setFearGreed]   = useState(null);
  const [fundingRates,setFundingRates]= useState({});
  const [deribitChain,setDeribitChain]= useState({ BTC: [], ETH: [] });
  const [deribitIV,   setDeribitIV]   = useState({ BTC: null, ETH: null });
  const [selected,    setSelected]    = useState("BTC");
  const [priceHistory,setPriceHistory]= useState({});
  const [statusMap,   setStatusMap]   = useState({});
  const [oiData,      setOiData]      = useState({});

  const setStatus = (k, v) => setStatusMap((p) => ({ ...p, [k]: v }));

  // ── CoinGecko prices + 7d chart ──
  const fetchPrices = useCallback(async () => {
    setStatus("prices", "loading");
    try {
      const ids = CRYPTO_ASSETS.map((a) => a.id).join(",");
      // ── BACKEND HOOK ──
      const res = await backendFetch(
        `https://api.coingecko.com/api/v3/coins/markets` +
        `?vs_currency=usd&ids=${ids}&order=market_cap_desc` +
        `&sparkline=true&price_change_percentage=1h,24h,7d`,
        `/api/crypto/prices?ids=${ids}`
      );
      if (!res.ok) throw new Error(`${res.status}`);
      const data = await res.json();
      const p = {};
      const h = {};
      // Backend returns { coins: [ ...CoinGecko market objects ] }; direct API returns a bare array.
      const coins = Array.isArray(data) ? data : (data?.coins || []);
      if (!coins.length) throw new Error("empty");
      coins.forEach((coin) => {
        p[coin.symbol.toUpperCase()] = {
          price:      coin.current_price,
          change1h:   coin.price_change_percentage_1h_in_currency,
          change24h:  coin.price_change_percentage_24h,
          change7d:   coin.price_change_percentage_7d_in_currency,
          marketCap:  coin.market_cap,
          volume24h:  coin.total_volume,
          high24h:    coin.high_24h,
          low24h:     coin.low_24h,
        };
        h[coin.symbol.toUpperCase()] = (coin.sparkline_in_7d?.price || []).map((v, i) => ({
          t: i, price: v,
        }));
      });
      setPrices(p);
      setPriceHistory(h);
      setStatus("prices", "live");
    } catch {
      // ⚠⚠ No stand-in prices (BTC 67,420 and seven others used to sit here and
      // went to the AI unmarked). A failed refresh clears the last prices too:
      // they would otherwise sit under an OFFLINE badge looking current.
      setPrices({});
      setPriceHistory({});
      setStatus("prices", "offline");
    }
  }, []);

  // ── Alternative.me Fear & Greed ──
  const fetchFearGreed = useCallback(async () => {
    setStatus("feargreed", "loading");
    try {
      // ── BACKEND HOOK ──
      const res = await backendFetch(
        "https://api.alternative.me/fng/?limit=30",
        "/api/crypto/feargreed?limit=30"
      );
      const data = await res.json();
      if (data.data?.length) {
        const current = data.data[0];
        const history = data.data.slice(0, 30).reverse().map((d) => ({
          date:  new Date(parseInt(d.timestamp) * 1000).toLocaleDateString("en", { month: "short", day: "numeric" }),
          value: parseInt(d.value),
          label: d.value_classification,
        }));
        setFearGreed({
          value:       parseInt(current.value),
          label:       current.value_classification,
          timestamp:   current.timestamp,
          history,
        });
        setStatus("feargreed", "live");
      } else throw new Error("empty");
    } catch {
      // ⚠⚠ No stand-in index (a fixed 64 with a random 30-day history used to
      // sit here). The panel shows "—".
      setFearGreed(null);
      setStatus("feargreed", "offline");
    }
  }, []);

  // ── Deribit options — BTC + ETH IV surface ──
  const fetchDeribitOptions = useCallback(async (currency) => {
    setStatus(`deribit_${currency}`, "loading");
    try {
      // ── BACKEND HOOK ──
      const res = await backendFetch(
        `https://www.deribit.com/api/v2/public/get_book_summary_by_currency?currency=${currency}&kind=option`,
        `/api/crypto/deribit/${currency}`
      );
      const data = await res.json();
      if (data.result?.length) {
        // Filter to most liquid options
        const options = data.result
          .filter((o) => o.mark_iv && o.open_interest > 0)
          .map((o) => {
            const parts = o.instrument_name.split("-");
            return {
              instrument: o.instrument_name,
              expiry:     parts[1],
              strike:     parseFloat(parts[2]),
              type:       parts[3], // C or P
              iv:         o.mark_iv,
              oi:         o.open_interest,
              bid:        o.bid_price,
              ask:        o.ask_price,
              midPrice:   o.mid_price,
              underlyingPrice: o.underlying_price,
            };
          })
          .slice(0, 60);

        setDeribitChain((prev) => ({ ...prev, [currency]: options }));

        // Extract ATM IV per expiry for term structure
        const byExpiry = {};
        options.forEach((o) => {
          if (!byExpiry[o.expiry]) byExpiry[o.expiry] = [];
          byExpiry[o.expiry].push(o);
        });
        const termStructure = Object.entries(byExpiry).map(([exp, opts]) => {
          const atmIVs = opts
            .filter((o) => Math.abs(o.strike / (o.underlyingPrice || 1) - 1) < 0.05)
            .map((o) => o.iv);
          return { expiry: exp, atmIV: atmIVs.length ? atmIVs.reduce((a, b) => a + b) / atmIVs.length : null };
        }).filter((t) => t.atmIV).slice(0, 8);

        setDeribitIV((prev) => ({ ...prev, [currency]: termStructure }));
        setStatus(`deribit_${currency}`, "live");
      } else throw new Error("empty");
    } catch {
      // ⚠⚠ No stand-in chain or term structure (2024 expiries with invented IVs
      // used to sit here). The panel shows "—" and no chart.
      setDeribitChain((prev) => ({ ...prev, [currency]: [] }));
      setDeribitIV((prev) => ({ ...prev, [currency]: null }));
      setStatus(`deribit_${currency}`, "offline");
    }
  }, []);

  // ── Funding rates (CoinGlass) ──
  const fetchFundingRates = useCallback(async () => {
    setStatus("funding", "loading");
    try {
      // ── BACKEND HOOK ──
      const res = await backendFetch(
        "https://open-api.coinglass.com/public/v2/funding?symbol=BTC,ETH,SOL",
        "/api/crypto/funding"
      );
      const data = await res.json();
      // Backend returns { source:"bybit-perp", rates: { BTC:{funding_rate,...}, ETH:{...}, SOL:{...} } }
      const src = data?.rates;
      if (!src) throw new Error("no funding rates");
      const rates = {};
      ["BTC", "ETH", "SOL"].forEach((sym) => {
        const r = src[sym];
        if (r && r.funding_rate != null) {
          rates[sym] = { bybit: r.funding_rate };
        }
      });
      setFundingRates(rates);
      setStatus("funding", "live");
    } catch {
      // ⚠⚠ No stand-in rates (0.0098 / 0.0091 / 0.0141 used to sit here and read
      // as "NORMAL LONG"). Each row shows "—".
      setFundingRates({});
      setStatus("funding", "offline");
    }
  }, []);

  useEffect(() => {
    fetchPrices();
    fetchFearGreed();
    fetchFundingRates();
    fetchDeribitOptions("BTC");
    fetchDeribitOptions("ETH");
    const t = setInterval(() => { fetchPrices(); fetchFundingRates(); }, 60_000);
    return () => clearInterval(t);
  }, []);

  // Funding rate signal interpretation
  const getFundingSignal = (rate) => {
    if (rate == null) return { label: "—", color: C.textDim };
    const annualized = rate * 3 * 365 * 100; // 8h rate → annual %
    if (rate > 0.05)  return { label: `EXTREME LONG (${fmt(annualized, 0)}% APR) — Contrarian bearish`, color: C.red    };
    if (rate > 0.02)  return { label: `ELEVATED LONG (${fmt(annualized, 0)}% APR) — Crowded`, color: C.amber  };
    if (rate > 0)     return { label: `NORMAL LONG (${fmt(annualized, 0)}% APR)`, color: C.green  };
    if (rate < -0.02) return { label: `ELEVATED SHORT (${fmt(annualized, 0)}% APR) — Crowded`, color: C.amber  };
    if (rate < 0)     return { label: `NEGATIVE (${fmt(annualized, 0)}% APR) — Contrarian bullish`, color: C.teal  };
    return { label: "NEUTRAL", color: C.textDim };
  };

  // Fear & Greed color
  const fgColor = fearGreed
    ? fearGreed.value >= 75 ? C.red
    : fearGreed.value >= 55 ? C.orange
    : fearGreed.value >= 45 ? C.amber
    : fearGreed.value >= 25 ? C.teal
    : C.green  // Extreme fear = contrarian green
    : C.textDim;

  const fgLabel = fearGreed
    ? fearGreed.value >= 75 ? "EXTREME GREED — Contrarian sell signal"
    : fearGreed.value >= 55 ? "GREED"
    : fearGreed.value >= 45 ? "NEUTRAL"
    : fearGreed.value >= 25 ? "FEAR"
    : "EXTREME FEAR — Contrarian buy signal"
    : "—";

  const selPrice = prices[selected];

  // ⚠⚠ Built from LIVE sections only. Each block is gated on its own status, so
  // a section that failed is stated as unavailable rather than sent as a number.
  // The button is hidden when no section is live.
  const isLive = (k) => statusMap[k] === "live";
  const anyLive = ["prices", "feargreed", "funding", "deribit_BTC", "deribit_ETH"].some(isLive);
  const atm30 = (c) =>
    isLive(`deribit_${c}`) ? deribitIV[c]?.find((t) => t.expiry.includes("30"))?.atmIV : null;
  const analystContext =
    `Crypto market snapshot:\n` +
    (isLive("prices")
      ? Object.entries(prices).map(([sym, p]) =>
          `${sym}: $${fmt(p.price)} (1h: ${pct(p.change1h)}, 24h: ${pct(p.change24h)}, 7d: ${pct(p.change7d)})`
        ).join("\n")
      : "Prices: unavailable") +
    `\n\nCrypto Fear & Greed: ${isLive("feargreed") && fearGreed
      ? `${fearGreed.value}/100 (${fearGreed.label})` : "unavailable"}\n` +
    `BTC funding rate (Bybit): ${isLive("funding") && fundingRates.BTC?.bybit != null ? fmt(fundingRates.BTC.bybit * 100, 4) + "% per 8h" : "unavailable"}\n` +
    `ETH funding rate (Bybit): ${isLive("funding") && fundingRates.ETH?.bybit != null ? fmt(fundingRates.ETH.bybit * 100, 4) + "% per 8h" : "unavailable"}\n` +
    `BTC options ATM IV (30D): ${atm30("BTC") != null ? `${atm30("BTC")}%` : "unavailable"}\n` +
    `ETH options ATM IV (30D): ${atm30("ETH") != null ? `${atm30("ETH")}%` : "unavailable"}\n\n` +
    `Analyze the cross-crypto picture: relative performance, what the funding rates imply about ` +
    `crowding and potential mean reversion, how Fear & Greed context modifies the signal, ` +
    `and what the Deribit IV term structure suggests about the market's vol expectations. ` +
    `Do not estimate any value marked unavailable.`;

  return (
    <div>
      {/* Top metrics row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 8, marginBottom: 10 }}>

        {/* Fear & Greed */}
        <div style={s.panel}>
          <div style={s.panelH}>
            <span style={s.panelT}>CRYPTO FEAR & GREED</span>
            <DataStatus status={statusMap.feargreed || "offline"} source="Crypto Fear & Greed" />
          </div>
          <div style={s.panelB}>
            <div style={{ fontSize: 11, color: C.textDim, marginBottom: 2 }}>INDEX (0=FEAR, 100=GREED)</div>
            <div style={{ fontSize: 36, fontWeight: 700, color: fgColor, lineHeight: 1 }}>
              {fearGreed?.value ?? "—"}
            </div>
            <div style={{ fontSize: 11, fontWeight: 700, color: fgColor, marginTop: 2, marginBottom: 8 }}>
              {fgLabel}
            </div>
            {/* Mini bar */}
            <div style={{ height: 5, background: C.border, borderRadius: 2, marginBottom: 8, overflow: "hidden" }}>
              <div style={{ width: `${fearGreed?.value ?? 0}%`, height: "100%", background: fgColor, transition: "width 0.5s" }} />
            </div>
            {fearGreed?.history && (
              <ResponsiveContainer width="100%" height={50}>
                <AreaChart data={fearGreed.history.slice(-14)}>
                  <defs>
                    <linearGradient id="fgGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor={fgColor} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={fgColor} stopOpacity={0}   />
                    </linearGradient>
                  </defs>
                  <ReferenceLine y={25} stroke={C.green  + "44"} />
                  <ReferenceLine y={75} stroke={C.red    + "44"} />
                  <Area type="monotone" dataKey="value" stroke={fgColor} strokeWidth={1.5} fill="url(#fgGrad)" dot={false} />
                  <Tooltip contentStyle={{ background: C.panel, border: `1px solid ${C.border}`, fontSize: 11 }}
                    formatter={(v) => [v, "Fear/Greed"]} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* BTC stats */}
        <div style={s.panel}>
          <div style={s.panelH}>
            <span style={s.panelT}>BITCOIN</span>
            <DataStatus status={statusMap.prices || "offline"} source="" />
          </div>
          <div style={s.panelB}>
            {prices.BTC ? (
              <>
                <div style={{ fontSize: 26, fontWeight: 700, color: C.amber }}>${fmtK(prices.BTC.price)}</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(64px, 1fr))", gap: 4, marginTop: 6 }}>
                  {[["1H", prices.BTC.change1h], ["24H", prices.BTC.change24h], ["7D", prices.BTC.change7d]].map(([label, val]) => (
                    <div key={label}>
                      <div style={{ fontSize: 10, color: C.textDim }}>{label}</div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: (val || 0) >= 0 ? C.green : C.red }}>{pct(val)}</div>
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: 11, color: C.textDim, marginTop: 6 }}>
                  MCap: {fmtB(prices.BTC.marketCap)} · Vol: {fmtB(prices.BTC.volume24h)}
                </div>
                {priceHistory.BTC?.length > 0 && (
                  <ResponsiveContainer width="100%" height={40}>
                    <LineChart data={priceHistory.BTC}>
                      <Line type="monotone" dataKey="price" stroke={C.amber} strokeWidth={1.5} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </>
            ) : <div style={{ color: C.textDim, fontSize: 13 }}>{statusMap.prices === "offline" ? "Unavailable" : "Loading..."}</div>}
          </div>
        </div>

        {/* ETH stats */}
        <div style={s.panel}>
          <div style={s.panelH}>
            <span style={s.panelT}>ETHEREUM</span>
            <DataStatus status={statusMap.prices || "offline"} source="" />
          </div>
          <div style={s.panelB}>
            {prices.ETH ? (
              <>
                <div style={{ fontSize: 26, fontWeight: 700, color: C.purple }}>${fmtK(prices.ETH.price)}</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(64px, 1fr))", gap: 4, marginTop: 6 }}>
                  {[["1H", prices.ETH.change1h], ["24H", prices.ETH.change24h], ["7D", prices.ETH.change7d]].map(([label, val]) => (
                    <div key={label}>
                      <div style={{ fontSize: 10, color: C.textDim }}>{label}</div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: (val || 0) >= 0 ? C.green : C.red }}>{pct(val)}</div>
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: 11, color: C.textDim, marginTop: 6 }}>
                  MCap: {fmtB(prices.ETH.marketCap)} · Vol: {fmtB(prices.ETH.volume24h)}
                </div>
                {priceHistory.ETH?.length > 0 && (
                  <ResponsiveContainer width="100%" height={40}>
                    <LineChart data={priceHistory.ETH}>
                      <Line type="monotone" dataKey="price" stroke={C.purple} strokeWidth={1.5} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </>
            ) : <div style={{ color: C.textDim, fontSize: 13 }}>{statusMap.prices === "offline" ? "Unavailable" : "Loading..."}</div>}
          </div>
        </div>

        {/* Funding rates */}
        <div style={s.panel}>
          <div style={s.panelH}>
            <span style={s.panelT}>FUNDING RATES</span>
            <DataStatus status={statusMap.funding || "offline"} source="" />
          </div>
          <div style={s.panelB}>
            <div style={{ fontSize: 11, color: C.textDim, marginBottom: 8, lineHeight: 1.6 }}>
              8h perpetual futures funding. Extreme positive = longs paying heavily = contrarian bearish. Negative = contrarian bullish.
            </div>
            {["BTC", "ETH", "SOL"].map((sym) => {
              const rate   = fundingRates[sym]?.bybit;
              const signal = getFundingSignal(rate);
              return (
                <div key={sym} style={{ marginBottom: 8 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
                    <span style={{ fontSize: 14, fontWeight: 700 }}>{sym}</span>
                    <span style={{ fontSize: 14, fontWeight: 700, color: signal.color }}>
                      {rate != null ? `${(rate * 100).toFixed(4)}%` : "—"}
                    </span>
                  </div>
                  <div style={{ fontSize: 11, color: signal.color, lineHeight: 1.4 }}>{signal.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Asset grid */}
      <div style={s.panel}>
        <div style={s.panelH}>
          <span style={s.panelT}>CRYPTO MARKET OVERVIEW</span>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <DataStatus status={statusMap.prices || "offline"} source="60s" />
            <button style={{ ...s.btn(), padding: "3px 7px" }} onClick={fetchPrices}>
              <RefreshCw size={10} />
            </button>
          </div>
        </div>
        <div style={s.panelB}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(104px, 1fr))", gap: 6, marginBottom: 10 }}>
            {CRYPTO_ASSETS.map((asset) => {
              const p    = prices[asset.symbol];
              const up24 = (p?.change24h || 0) >= 0;
              const sel  = selected === asset.symbol;
              return (
                <div
                  key={asset.symbol}
                  onClick={() => setSelected(asset.symbol)}
                  style={{
                    background: C.panelAlt,
                    border: `1px solid ${sel ? C.amber : C.border}`,
                    borderRadius: 3,
                    padding: "8px 10px",
                    cursor: "pointer",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                    <span style={{ fontSize: 15, fontWeight: 700, color: sel ? C.amber : C.text }}>{asset.symbol}</span>
                    {p?.change24h != null && <span style={s.badge(up24)}>{pct(p.change24h)}</span>}
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: up24 ? C.green : C.red }}>
                    ${p?.price >= 1000 ? fmtK(p.price) : p?.price ? fmt(p.price) : "—"}
                  </div>
                  <div style={{ fontSize: 11, color: C.textDim, marginTop: 2 }}>
                    {p?.volume24h ? `Vol: ${fmtB(p.volume24h)}` : ""}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Selected asset 7d sparkline */}
          {priceHistory[selected]?.length > 0 && (
            <>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.amber, letterSpacing: "0.1em", marginBottom: 6 }}>
                {selected} — 7D PRICE (168 HOURS)
              </div>
              <ResponsiveContainer width="100%" height={120}>
                <AreaChart data={priceHistory[selected]}>
                  <defs>
                    <linearGradient id="cryptoGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor={C.amber} stopOpacity={0.2} />
                      <stop offset="95%" stopColor={C.amber} stopOpacity={0}   />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="t" hide />
                  <YAxis tick={{ fontSize: 10, fill: C.textDim }} tickLine={false} axisLine={false} domain={["auto", "auto"]} width={50} />
                  <Tooltip contentStyle={{ background: C.panel, border: `1px solid ${C.border}`, fontSize: 11 }}
                    formatter={(v) => [`$${fmt(v)}`, selected]} />
                  <Area type="monotone" dataKey="price" stroke={C.amber} strokeWidth={1.5} fill="url(#cryptoGrad)" dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            </>
          )}
        </div>
      </div>

      {/* Deribit Options — IV Term Structure */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 10, marginBottom: 10 }}>
        {DERIBIT_INSTRUMENTS.map((currency) => {
          const termData = deribitIV[currency] || [];
          const chain    = deribitChain[currency] || [];
          const spotP    = prices[currency]?.price;

          // Put/Call OI ratio from Deribit chain
          const putOI  = chain.filter((o) => o.type === "P").reduce((a, o) => a + o.oi, 0);
          const callOI = chain.filter((o) => o.type === "C").reduce((a, o) => a + o.oi, 0);
          const pcRatio = callOI > 0 ? (putOI / callOI).toFixed(2) : "—";

          // IV skew — OTM puts vs OTM calls
          const otmPutIV  = chain.filter((o) => o.type === "P" && spotP && o.strike < spotP * 0.9).map((o) => o.iv);
          const otmCallIV = chain.filter((o) => o.type === "C" && spotP && o.strike > spotP * 1.1).map((o) => o.iv);
          const avgPutIV  = otmPutIV.length  ? otmPutIV.reduce((a, b)  => a + b) / otmPutIV.length  : null;
          const avgCallIV = otmCallIV.length ? otmCallIV.reduce((a, b) => a + b) / otmCallIV.length : null;
          const skew      = avgPutIV && avgCallIV ? avgPutIV / avgCallIV : null;

          // Term structure inversion check (short IV > long IV = fear)
          const isInverted = termData.length >= 2 && termData[0]?.atmIV > termData[termData.length - 1]?.atmIV;

          return (
            <div key={currency} style={s.panel}>
              <div style={s.panelH}>
                <span style={s.panelT}>{currency} OPTIONS</span>
                <DataStatus status={statusMap[`deribit_${currency}`] || "offline"} source="" />
              </div>
              <div style={s.panelB}>
                {/* Quick stats */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(88px, 1fr))", gap: 6, marginBottom: 10 }}>
                  <div>
                    <div style={s.lbl}>PUT/CALL OI</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: parseFloat(pcRatio) > 1.2 ? C.red : parseFloat(pcRatio) < 0.7 ? C.green : C.amber }}>
                      {pcRatio}
                    </div>
                  </div>
                  <div>
                    <div style={s.lbl}>OTM PUT IV</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: C.red }}>
                      {avgPutIV ? `${fmt(avgPutIV, 1)}%` : "—"}
                    </div>
                  </div>
                  <div>
                    <div style={s.lbl}>IV SKEW (P/C)</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: skew && skew > 1.3 ? C.red : C.amber }}>
                      {skew ? `${fmt(skew, 2)}x` : "—"}
                    </div>
                  </div>
                </div>

                {/* Term structure chart */}
                {termData.length > 0 && (
                  <>
                    <div style={{ fontSize: 11, color: C.textDim, marginBottom: 4 }}>
                      IV Term Structure {isInverted ? "— ⚠ INVERTED (short IV > long IV = fear)" : "— Normal (upward sloping)"}
                    </div>
                    <ResponsiveContainer width="100%" height={100}>
                      <LineChart data={termData}>
                        <XAxis dataKey="expiry" tick={{ fontSize: 10, fill: C.textDim }} tickLine={false} />
                        <YAxis tick={{ fontSize: 10, fill: C.textDim }} tickLine={false} axisLine={false}
                          tickFormatter={(v) => `${v}%`} domain={["auto", "auto"]} width={32} />
                        <Tooltip contentStyle={{ background: C.panel, border: `1px solid ${C.border}`, fontSize: 11 }}
                          formatter={(v) => [`${fmt(v, 1)}%`, "ATM IV"]} />
                        <Line type="monotone" dataKey="atmIV"
                          stroke={isInverted ? C.red : C.teal} strokeWidth={2}
                          dot={{ fill: isInverted ? C.red : C.teal, r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </>
                )}

                {/* Top OI strikes */}
                {chain.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ fontSize: 11, color: C.textDim, marginBottom: 4 }}>TOP OI STRIKES</div>
                    {chain
                      .sort((a, b) => b.oi - a.oi)
                      .slice(0, 4)
                      .map((o, i) => (
                        <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "3px 0", borderBottom: `1px solid ${C.border}`, fontSize: 13 }}>
                          <span style={{ color: o.type === "C" ? C.green : C.red }}>
                            {o.type === "C" ? "CALL" : "PUT"} ${fmtK(o.strike)}
                          </span>
                          <span style={{ color: C.textMid }}>{fmtK(o.oi)} OI</span>
                          <span style={{ color: C.textDim }}>{fmt(o.iv, 1)}% IV</span>
                          <span style={{ color: C.textDim, fontSize: 11 }}>{o.expiry}</span>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Analyst */}
      {(keys.anthropicKey || window.QUANT_SIGNAL_BACKEND) && anyLive && (
        <div style={{ marginBottom: 10 }}>
          <AnalystButton
            panel="sentiment"
            label="SYNTHESIZE"
            context={analystContext}
          />
        </div>
      )}

      <div style={{ fontSize: 11, color: C.textDim }}>
        <span style={{ color: "#FFD700", fontWeight: 700 }}>Not a recommendation.</span>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// SENTIMENT TAB
// ═══════════════════════════════════════════════════════════════════════════════

// ── QS Fear/Greed Index panel (platform composite) ───────────────────────────
// ⚠⚠ THESE NAME WHAT THE METRIC MEANS, NOT HOW IT IS CALCULATED. The methodology
// is not published: no vendor, series id, ticker or endpoint belongs in any
// string a reader can see, and no tooltip, (?) icon or hover text exists to
// restore one. The raw value renders beside the name, so the number stays
// checkable without naming its source.
const QS_COMPONENT_META = [
  { key: "vix",          name: "Volatility Fear" },
  { key: "hy_spread",    name: "Credit Stress" },
  { key: "put_call",     name: "Hedging Pressure" },
  { key: "spy_momentum", name: "Price Momentum" },
  { key: "breadth",      name: "Institutional Flow" },
  // ⚠⚠ SIGN CONVENTION, BECAUSE THIS ONE READS BACKWARDS TO INTUITION. The value
  // is VIX / VXVCLS — near-term implied vol over three-month. A ratio ABOVE 1.0 is
  // backwardation: the options market is pricing more risk into the next month
  // than into the next quarter, which is genuine stress, not complacency. Below
  // 1.0 is contango and the ordinary calm state.
  // ⚠ INVERTED IN SCORING. Higher ratio = more fear = LOWER Fear/Greed score, the
  // same direction as VIX, HY spread and put/call. The backend owns that sign
  // (COMPONENT_DIRECTION); this row only renders the raw ratio and whatever score
  // came back, so the two can never disagree — but read the number here knowing
  // that a rising value pushes the gauge DOWN.
  { key: "term_structure", name: "Fear Persistence" },
];

// 0 fear (red) → 100 greed (green).
const qsColor = (v) => v == null ? C.textDim
  : v <= 20 ? C.red : v <= 40 ? C.orange : v <= 60 ? C.amber : v <= 80 ? C.teal : C.green;

function QSArcGauge({ value }) {
  const cx = 100, cy = 105, r = 75;
  // Upper semicircle (sweep-flag 1): the arc the needle actually sweeps. The old
  // path used sweep-flag 0 → bottom semicircle, clipped below the viewBox.
  const arc = `M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`;
  // score → angle: 0 = -180° (left), 100 = 0° (right). Point on radius R:
  const angRad = (s) => (-180 + (s / 100) * 180) * (Math.PI / 180);
  const pt = (s, R) => [cx + R * Math.cos(angRad(s)), cy + R * Math.sin(angRad(s))];
  const OUTER = r - 7;                        // inner edge of the colored band
  const MAJORS = [0, 25, 50, 75, 100];
  const MINORS = [10, 20, 30, 40, 60, 70, 80, 90];   // every 10, majors excluded
  const v = value == null ? null : Math.max(0, Math.min(100, value));
  const rotation = v == null ? 0 : (v / 100) * 180 - 180;
  return (
    <svg viewBox="0 0 200 130" style={{ width: "100%", maxWidth: 320, display: "block", margin: "0 auto" }}>
      <rect width="200" height="130" fill={C.panel} rx="8" />
      <defs>
        <linearGradient id="speedoGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%"   stopColor="#ef4444" />
          <stop offset="25%"  stopColor="#f97316" />
          <stop offset="50%"  stopColor="#eab308" />
          <stop offset="75%"  stopColor="#84cc16" />
          <stop offset="100%" stopColor="#22c55e" />
        </linearGradient>
      </defs>

      {/* Track + colored gradient arc */}
      <path d={arc} fill="none" stroke={C.border} strokeWidth={18} strokeLinecap="butt" />
      <path d={arc} fill="none" stroke="url(#speedoGradient)" strokeWidth={14} strokeLinecap="butt" />

      {/* Minor ticks (every 10 units) */}
      {MINORS.map((s) => {
        const [x1, y1] = pt(s, OUTER);
        const [x2, y2] = pt(s, OUTER - 5);
        return <line key={`mn-${s}`} x1={x1} y1={y1} x2={x2} y2={y2} stroke={C.textDim} strokeWidth={1} />;
      })}

      {/* Major ticks + labels (0/25/50/75/100) */}
      {MAJORS.map((s) => {
        const [x1, y1] = pt(s, OUTER);
        const [x2, y2] = pt(s, OUTER - 10);
        const [lx, ly] = pt(s, r + 11);
        return (
          <g key={`mj-${s}`}>
            <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={C.text} strokeWidth={1.6} />
            <text x={lx} y={ly + 3} fontSize="9" fill={C.text} textAnchor="middle">{s}</text>
          </g>
        );
      })}

      {/* Needle + hub — a 0°-direction line rotated to the score */}
      {v != null && (
        <>
          <line
            x1={cx} y1={cy} x2={cx + r - 6} y2={cy}
            stroke={C.text} strokeWidth={2} strokeLinecap="round"
            transform={`rotate(${rotation} ${cx} ${cy})`}
          />
          <circle cx={cx} cy={cy} r={5} fill={C.text} />
        </>
      )}
    </svg>
  );
}

function QSFearGreedPanel({ qsfg, status, keys }) {
  const composite = qsfg && typeof qsfg.composite === "number" ? qsfg.composite : null;
  const comps = (qsfg && qsfg.components) || {};
  const available = qsfg?.data_quality?.components_available ?? 0;
  const col = qsColor(composite);
  const updated = qsfg?.computed_at ? (() => {
    try {
      return new Date(qsfg.computed_at).toLocaleTimeString("en-US",
        { timeZone: "America/New_York", hour: "2-digit", minute: "2-digit", hour12: false }) + " ET";
    } catch { return null; }
  })() : null;

  const synthContext =
    `QS Fear/Greed Index — composite ${composite ?? "N/A"}/100 (${qsfg?.label ?? "unavailable"}), ` +
    `${available}/6 signals active.\n` +
    QS_COMPONENT_META.map(({ key, name }) => {
      const c = comps[key];
      return c ? `${name}: ${c.score}/100 (value ${c.value ?? "—"}, ${c.label})` : `${name}: awaiting data`;
    }).join("\n") +
    `\n\nWhat does the current fear/greed reading suggest about market positioning?`;

  return (
    <div style={{ ...s.panel, marginBottom: 10 }}>
      <div style={s.panelH}>
        <span style={s.panelT}>QS FEAR/GREED INDEX</span>
        <DataStatus status={status || "offline"} source="Powered by platform data" />
      </div>
      <div style={s.panelB}>
        {composite == null && status !== "loading" ? (
          <div style={{ fontSize: 13, color: C.textDim, textAlign: "center", padding: 12 }}>
            Fear/Greed index unavailable — awaiting market data. {available}/6 signals active.
          </div>
        ) : (
          <>
            <QSArcGauge value={composite} />
            <div style={{ textAlign: "center", marginTop: 4, marginBottom: 12 }}>
              <div style={{ fontSize: 44, fontWeight: 700, lineHeight: 1, color: col }}>
                {composite != null ? Math.round(composite) : "—"}
              </div>
              <div style={{ fontSize: 15, fontWeight: 700, color: col, marginTop: 4, letterSpacing: "0.05em" }}>
                {qsfg?.label ?? (status === "loading" ? "LOADING…" : "UNAVAILABLE")}
              </div>
            </div>

            <div style={{ display: "grid", gap: 6 }}>
              {QS_COMPONENT_META.map(({ key, name }) => {
                const c = comps[key];
                const has = c && typeof c.score === "number";
                return (
                  <div key={key} style={{ opacity: has ? 1 : 0.5 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", fontSize: 11, marginBottom: 2 }}>
                      <span style={{ color: C.textMid }}>{name}</span>
                      <span style={{ color: C.textDim }}>
                        {has
                          ? <>value <span style={{ color: C.text }}>{c.value ?? "—"}</span> · <span style={{ fontWeight: 700, color: qsColor(c.score) }}>{Math.round(c.score)}</span></>
                          : <span style={{ fontStyle: "italic" }}>— · Awaiting data</span>}
                      </span>
                    </div>
                    {/* ⚠ FLOOR OF 2, NOT 0: a component that scores exactly 0 has REAL
                        data — put/call is the one that reaches it routinely, because
                        _lerp clamps at a ratio of 1.2 and SPY printed 1.215 on
                        2026-08-06. At a 0 floor the fill rendered zero-width, i.e.
                        pixel-identical to the "no data" track, while the row above it
                        read "value 1.215 · 0". The floor only applies when `has` is
                        true, so a genuinely absent component still shows an empty
                        track. Same trick as the GEX chart's 2px minimum bar. */}
                    <div style={{ height: 5, background: C.border, borderRadius: 2, overflow: "hidden" }}>
                      <div style={{ width: has ? `${Math.max(2, Math.min(100, c.score))}%` : "0%", height: "100%", background: has ? qsColor(c.score) : C.border }} />
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10, fontSize: 10, color: C.textDim }}>
              <span>{available}/6 signals active</span>
              {updated && <span>Updated {updated}</span>}
            </div>

            {(keys.anthropicKey || window.QUANT_SIGNAL_BACKEND) && (
              <div style={{ marginTop: 10 }}>
                <AnalystButton panel="sentiment" label="SYNTHESIZE" context={synthContext} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── QS Bull/Bear Indicator (proprietary positioning composite) ───────────────
// ⚠⚠ THIS GAUGE IS INVERTED RELATIVE TO THE FEAR/GREED ONE ABOVE AND THAT IS THE
// WHOLE POINT. There, 100 is greed and paints green. Here 10 is CROWDED LONG and
// paints RED, because a crowded long is where positioning risk lives. The two
// gauges sit on the same screen, so they must never share a colour function or a
// gradient — qsColor/speedoGradient are Fear/Greed's and stay untouched.
const QS_BB_COMPONENT_META = [
  { key: "ad_breadth", name: "Market Breadth",
    fmt: (v) => `${(v * 100).toFixed(1)}% adv` },
  { key: "rotation",   name: "Risk Appetite",
    fmt: (v) => `${v >= 0 ? "+" : ""}${v.toFixed(2)}% / wk` },
  { key: "credit",     name: "Credit Direction",
    fmt: (v) => `${v >= 0 ? "+" : ""}${v.toFixed(2)}% / wk` },
  // ⚠⚠ RENAMED FROM "flow" (dark-pool buyer ratio) ON 2026-08-20. The old
  // component read dp:market:aggregate with the identical formula as QS
  // Fear/Greed's breadth component — the two gauges were sharing an input while
  // being presented as independent reads. Dark-pool breadth now lives in
  // Fear/Greed alone; this slot measures options flow instead.
  // ⚠ "% calls", NOT "% buy". The value is call signals / (call + put signals)
  // from stream:uo:market, not a share of buyer-initiated prints. Leaving the old
  // suffix would have labelled a correct number with the wrong unit, which is
  // harder to catch than a blank.
  { key: "options_flow", name: "Options Bias",
    fmt: (v) => `${(v * 100).toFixed(1)}% calls` },
];

// 0 capitulation (green, contrarian buy) → 10 crowded long (red, contrarian sell).
// ⚠ Thresholds mirror _bb_label on the backend exactly: 8 / 6 / 4 / 2. If those
// bands move, this moves with them or the colour will contradict the label.
const bbColor = (v) => v == null ? C.textDim
  : v >= 8 ? C.red : v >= 6 ? C.orange : v >= 4 ? C.amber : v > 2 ? C.teal : C.green;

function BullBearGauge({ value }) {
  const cx = 100, cy = 105, r = 75;
  const arc = `M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`;
  // ⚠ DOMAIN IS 0-10, not 0-100. Feeding a Fear/Greed composite in here would
  // peg the needle at the right-hand stop and read CROWDED LONG regardless.
  const angRad = (sc) => (-180 + (sc / 10) * 180) * (Math.PI / 180);
  const pt = (sc, R) => [cx + R * Math.cos(angRad(sc)), cy + R * Math.sin(angRad(sc))];
  const OUTER = r - 7;
  // Majors land on the band boundaries so the ticks read as the thresholds they are.
  const MAJORS = [0, 2, 4, 6, 8, 10];
  const MINORS = [1, 3, 5, 7, 9];
  const v = value == null ? null : Math.max(0, Math.min(10, value));
  const rotation = v == null ? 0 : (v / 10) * 180 - 180;
  return (
    <svg viewBox="0 0 200 130" style={{ width: "100%", maxWidth: 320, display: "block", margin: "0 auto" }}>
      <rect width="200" height="130" fill={C.panel} rx="8" />
      <defs>
        {/* ⚠ OWN GRADIENT ID. SVG gradient ids are document-global — reusing
            "speedoGradient" would have this arc silently adopt Fear/Greed's
            red→green ramp, i.e. exactly backwards. */}
        <linearGradient id="bullBearGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%"   stopColor="#22c55e" />
          <stop offset="25%"  stopColor="#84cc16" />
          <stop offset="50%"  stopColor="#eab308" />
          <stop offset="75%"  stopColor="#f97316" />
          <stop offset="100%" stopColor="#ef4444" />
        </linearGradient>
      </defs>

      <path d={arc} fill="none" stroke={C.border} strokeWidth={18} strokeLinecap="butt" />
      <path d={arc} fill="none" stroke="url(#bullBearGradient)" strokeWidth={14} strokeLinecap="butt" />

      {MINORS.map((sc) => {
        const [x1, y1] = pt(sc, OUTER);
        const [x2, y2] = pt(sc, OUTER - 5);
        return <line key={`bb-mn-${sc}`} x1={x1} y1={y1} x2={x2} y2={y2} stroke={C.textDim} strokeWidth={1} />;
      })}

      {MAJORS.map((sc) => {
        const [x1, y1] = pt(sc, OUTER);
        const [x2, y2] = pt(sc, OUTER - 10);
        const [lx, ly] = pt(sc, r + 11);
        return (
          <g key={`bb-mj-${sc}`}>
            <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={C.text} strokeWidth={1.6} />
            <text x={lx} y={ly + 3} fontSize="9" fill={C.text} textAnchor="middle">{sc}</text>
          </g>
        );
      })}

      {v != null && (
        <>
          <line
            x1={cx} y1={cy} x2={cx + r - 6} y2={cy}
            stroke={C.text} strokeWidth={2} strokeLinecap="round"
            transform={`rotate(${rotation} ${cx} ${cy})`}
          />
          <circle cx={cx} cy={cy} r={5} fill={C.text} />
        </>
      )}
    </svg>
  );
}

const BB_SIGNAL_TEXT = {
  CONTRARIAN_BEARISH: "CONTRARIAN SELL — positioning is crowded long",
  CONTRARIAN_BULLISH: "CONTRARIAN BUY — positioning has capitulated",
};

function QSBullBearPanel({ bullbear, status, keys }) {
  const score = bullbear && typeof bullbear.score === "number" ? bullbear.score : null;
  const comps = (bullbear && bullbear.components) || {};
  const available = bullbear?.components_available ?? 0;
  const col = bbColor(score);
  const signal = bullbear?.signal ?? null;
  const method = bullbear?.bull_bear_method ?? null;
  const updated = bullbear?.computed_at ? (() => {
    try {
      return new Date(bullbear.computed_at).toLocaleTimeString("en-US",
        { timeZone: "America/New_York", hour: "2-digit", minute: "2-digit", hour12: false }) + " ET";
    } catch { return null; }
  })() : null;

  const synthContext =
    `QS Bull/Bear Indicator — ${score ?? "N/A"}/10 (${bullbear?.label ?? "unavailable"}), ` +
    `${available}/4 components active, scoring method ${method ?? "n/a"}.\n` +
    `This is a CONTRARIAN positioning read: 10 is crowded long and bearish, 0 is capitulation and bullish.\n` +
    QS_BB_COMPONENT_META.map(({ key, name, fmt }) => {
      const c = comps[key];
      return c && typeof c.score === "number"
        ? `${name}: ${c.score}/10 (raw ${c.value != null ? fmt(c.value) : "—"}${c.zscore != null ? `, z ${c.zscore}` : ""})`
        : `${name}: awaiting data`;
    }).join("\n") +
    `\n\nWhat does this positioning reading imply about crowding risk right now?`;

  return (
    <div style={{ ...s.panel, marginBottom: 10 }}>
      <div style={s.panelH}>
        <span style={s.panelT}>QS BULL/BEAR INDICATOR</span>
        <DataStatus status={status || "offline"} source="Powered by platform data" />
      </div>
      <div style={s.panelB}>
        <div style={{ fontSize: 12, color: C.textDim, marginBottom: 8, lineHeight: 1.6 }}>
          Proprietary positioning composite, 0–10. Read it <strong style={{ color: C.textMid }}>contrarian</strong>:
          above 8 is crowded long (risk), below 2 is capitulation (opportunity).
          Between 2 and 8 it carries no signal by design.
        </div>
        {score == null && status !== "loading" ? (
          <div style={{ fontSize: 13, color: C.textDim, textAlign: "center", padding: 12 }}>
            Bull/Bear indicator unavailable — awaiting market data. {available}/4 components active.
          </div>
        ) : (
          <>
            <BullBearGauge value={score} />
            <div style={{ textAlign: "center", marginTop: 4, marginBottom: 12 }}>
              <div style={{ fontSize: 44, fontWeight: 700, lineHeight: 1, color: col }}>
                {score != null ? score.toFixed(1) : "—"}
              </div>
              <div style={{ fontSize: 15, fontWeight: 700, color: col, marginTop: 4, letterSpacing: "0.05em" }}>
                {bullbear?.label ?? (status === "loading" ? "LOADING…" : "UNAVAILABLE")}
              </div>
              {/* ⚠ The signal is rendered from the backend field, never inferred from
                  the score here — one set of band thresholds, on the server. */}
              {signal && (
                <div style={{ fontSize: 12, fontWeight: 700, color: col, marginTop: 6 }}>
                  {BB_SIGNAL_TEXT[signal] || signal}
                </div>
              )}
            </div>

            <div style={{ display: "grid", gap: 6 }}>
              {QS_BB_COMPONENT_META.map(({ key, name, fmt }) => {
                const c = comps[key];
                const has = c && typeof c.score === "number";
                return (
                  <div key={key} style={{ opacity: has ? 1 : 0.5 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", fontSize: 11, marginBottom: 2 }}>
                      <span style={{ color: C.textMid }}>{name}</span>
                      <span style={{ color: C.textDim }}>
                        {has
                          ? <>value <span style={{ color: C.text }}>{c.value != null ? fmt(c.value) : "—"}</span> · <span style={{ fontWeight: 700, color: bbColor(c.score) }}>{c.score.toFixed(1)}</span></>
                          : <span style={{ fontStyle: "italic" }}>— · Awaiting data</span>}
                      </span>
                    </div>
                    {/* Same 2% floor as the Fear/Greed rows, and for the same reason:
                        a real 0.0 must not render pixel-identical to "no data". */}
                    <div style={{ height: 5, background: C.border, borderRadius: 2, overflow: "hidden" }}>
                      <div style={{ width: has ? `${Math.max(2, Math.min(100, c.score * 10))}%` : "0%", height: "100%", background: has ? bbColor(c.score) : C.border }} />
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10, fontSize: 10, color: C.textDim }}>
              <span>
                {available}/4 components active
                {method ? ` · ${method}` : ""}
              </span>
              {updated && <span>Updated {updated}</span>}
            </div>

            {(keys.anthropicKey || window.QUANT_SIGNAL_BACKEND) && (
              <div style={{ marginTop: 10 }}>
                <AnalystButton panel="sentiment" label="SYNTHESIZE" context={synthContext} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── Market Stress Monitor ────────────────────────────────────────────────────
// Sequential stress detection over the QS Fear/Greed z-scores. The backend owns
// every threshold and every message; this renders what it is told.
//
// ⚠⚠ NOTHING HERE RE-DERIVES A LEVEL FROM THE Z-SCORES. The four numbers on
// screen are for the reader's judgement, not for the component's — the level,
// the colour, the headline and the per-input badges all arrive decided. Two
// copies of a threshold is two chances for the panel to contradict the API it
// is displaying, and the badge is the half a user would believe.
const STRESS_COLORS = {
  green:  C.green,
  amber:  C.amber,
  orange: C.orange,
  red:    C.red,
};

// The level's visual prefix. Escalating glyph count is the point: it reads at a
// glance from across a room, which is what a stress monitor is for.
const STRESS_GLYPH = { 0: "●", 1: "⚠", 2: "⚠⚠", 3: "⚠⚠⚠" };

// ⚠ Display order is fixed HERE rather than taken from Object.keys(inputs) —
// JSON object ordering is not a contract, and a reordered payload silently
// reshuffling the rows would make the panel look unstable between refreshes.
const STRESS_INPUT_ORDER = ["hy_spread", "vix", "put_call", "breadth"];

function StressRow({ row }) {
  if (!row) return null;
  const available = row.available === true;
  const stressed = row.stressed === true;
  // Three states, not two: stressed, clear, and NOT KNOWN. Rendering an unknown
  // as a green check would assert the condition is clear when nothing has been
  // measured — the exact failure the backend refuses to make.
  const tint = !available ? C.textDim : stressed ? C.red : C.green;
  return (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "baseline",
      fontSize: 12, padding: "3px 0", opacity: available ? 1 : 0.55,
    }}>
      <span style={{ color: C.textMid, minWidth: 90 }}>{row.label}</span>
      <span style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        {available ? (
          <>
            <span style={{ color: C.textDim, fontSize: 10 }}>{row.threshold}</span>
            <span style={{
              color: C.text, fontWeight: 700, fontVariantNumeric: "tabular-nums",
              minWidth: 48, textAlign: "right",
            }}>
              {row.zscore >= 0 ? "+" : ""}{Number(row.zscore).toFixed(2)}
            </span>
            <span style={{ color: tint, fontWeight: 700, minWidth: 14, textAlign: "center" }}>
              {stressed ? "⚠" : "✓"}
            </span>
          </>
        ) : (
          // ⚠ The observation count is the whole reason this row is not blank.
          // "insufficient history" alone reads as broken; "8 obs" tells the user
          // it is filling and roughly when it will not be.
          <span style={{ color: C.textDim, fontStyle: "italic", fontSize: 11 }}>
            insufficient history · {row.obs ?? 0} obs
          </span>
        )}
      </span>
    </div>
  );
}

function MarketStressPanel({ stress, status }) {
  const calibrating = stress?.calibrating === true;
  const level = typeof stress?.level === "number" ? stress.level : null;
  const col = STRESS_COLORS[stress?.color] || C.textDim;
  const inputs = (stress && stress.inputs) || {};
  const unevaluated = Array.isArray(stress?.unevaluated_levels) ? stress.unevaluated_levels : [];
  const updated = stress?.computed_at ? (() => {
    try {
      return new Date(stress.computed_at).toLocaleTimeString("en-US",
        { timeZone: "America/New_York", hour: "2-digit", minute: "2-digit", hour12: false }) + " ET";
    } catch { return null; }
  })() : null;

  const unreadable = !stress || (level == null && !calibrating);

  return (
    <div style={{ ...s.panel, marginBottom: 10 }}>
      <div style={s.panelH}>
        <span style={s.panelT}>MARKET STRESS MONITOR</span>
        <DataStatus status={status || "offline"} source="Powered by platform data" />
      </div>
      <div style={s.panelB}>
        {unreadable ? (
          <div style={{ fontSize: 13, color: C.textDim, textAlign: "center", padding: 12 }}>
            {status === "loading" ? "Loading…" : "Stress monitor unavailable — awaiting sentiment data."}
          </div>
        ) : calibrating ? (
          /* ⚠ NO LEVEL AND NO CONDITIONS WHILE CALIBRATING, deliberately. Showing
             "NORMAL" with a row of green checks during calibration would be a
             claim the data cannot support — and it is the reassuring direction,
             which is the expensive one to be wrong in. */
          <div style={{ padding: "14px 6px", textAlign: "center" }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.textMid, letterSpacing: "0.05em" }}>
              CALIBRATING
            </div>
            <div style={{ fontSize: 12, color: C.textDim, marginTop: 8, lineHeight: 1.6 }}>
              Calibrating stress monitor. Available after sufficient market history accumulates.
            </div>
            {Number.isFinite(stress?.zscore_window) && Number.isFinite(stress?.window_required) && (
              <div style={{ fontSize: 11, color: C.textDim, marginTop: 8 }}>
                {stress.zscore_window} of {stress.window_required} sessions
              </div>
            )}
          </div>
        ) : (
          <>
            <div style={{
              display: "flex", alignItems: "center", gap: 12, marginBottom: 12,
              paddingBottom: 12, borderBottom: `1px solid ${C.border}`,
            }}>
              <span style={{ fontSize: 22, color: col, lineHeight: 1, letterSpacing: "-0.05em" }}>
                {STRESS_GLYPH[level] ?? "●"}
              </span>
              <span>
                <div style={{ fontSize: 20, fontWeight: 700, color: col, lineHeight: 1.1 }}>
                  {level === 0 ? "NORMAL" : `LEVEL ${level}`}
                </div>
                <div style={{ fontSize: 12, fontWeight: 700, color: col, letterSpacing: "0.05em", marginTop: 2 }}>
                  {stress.headline || ""}
                </div>
              </span>
            </div>

            <div style={{ display: "grid", gap: 2 }}>
              {STRESS_INPUT_ORDER.map((key) => (
                <StressRow key={key} row={inputs[key]} />
              ))}
            </div>

            <div style={{
              fontSize: 12, color: C.textDim, fontStyle: "italic",
              marginTop: 12, lineHeight: 1.6,
            }}>
              {stress.message || ""}
            </div>

            {/* ⚠ Named out loud rather than left as a blank row above. A level
                that could not be checked is not a level that came back clear,
                and the user is entitled to know which of the three is dark. */}
            {unevaluated.length > 0 && (
              <div style={{ fontSize: 11, color: C.textDim, marginTop: 6, lineHeight: 1.5 }}>
                Level{unevaluated.length > 1 ? "s" : ""} {unevaluated.join(", ")} not
                evaluated — insufficient history on one or more inputs.
              </div>
            )}

            {updated && (
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10, fontSize: 10, color: C.textDim }}>
                <span>Updated {updated}</span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export function SentimentTab({ keys, onData, globalSignals }) {
  const [cryptoFG,   setCryptoFG]   = useState(null);
  const [qsfg,       setQsfg]        = useState(null);
  const [bullbear,   setBullbear]    = useState(null);
  const [stress,     setStress]      = useState(null);
  const [pcHistory,  setPcHistory]   = useState([]);
  const [statusMap,  setStatusMap]   = useState({});

  const setStatus = (k, v) => setStatusMap((p) => ({ ...p, [k]: v }));

  // ── Crypto Fear & Greed ──
  useEffect(() => {
    setStatus("cryptofg", "loading");
    backendFetch("https://api.alternative.me/fng/?limit=1", "/api/crypto/feargreed?limit=1")
      .then((r) => r.json())
      .then((data) => {
        if (data.data?.[0]) {
          setCryptoFG({ value: parseInt(data.data[0].value), label: data.data[0].value_classification });
          setStatus("cryptofg", "live");
        } else throw new Error("empty");
      })
      .catch(() => {
        // ⚠ No stand-in reading (a fixed 64 "Greed" used to sit here).
        setCryptoFG(null);
        setStatus("cryptofg", "offline");
      });
  }, []);

  // ── QS Fear/Greed Index (platform composite) — fetch on mount + every 5 min ──
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      setStatus("qsfg", "loading");
      backendFetch("https://data.alpaca.markets", "/api/sentiment/qsfg")
        .then((r) => r.json())
        .then((data) => {
          if (cancelled) return;
          setQsfg(data && typeof data === "object" ? data : null);
          setStatus("qsfg", data && data.composite != null ? "live" : "offline");
        })
        .catch(() => { if (!cancelled) setStatus("qsfg", "offline"); });
    };
    load();
    const id = setInterval(load, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // ── QS Bull/Bear Indicator — fetch on mount + every 5 min ──
  // ⚠ Matches the backend cache TTL (300s). Polling faster only re-serves the
  // same cached object; polling slower would let the gauge lag the source it
  // claims to reflect.
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      setStatus("bullbear", "loading");
      backendFetch("https://data.alpaca.markets", "/api/sentiment/bullbear")
        .then((r) => r.json())
        .then((data) => {
          if (cancelled) return;
          setBullbear(data && typeof data === "object" ? data : null);
          setStatus("bullbear", data && data.score != null ? "live" : "offline");
        })
        .catch(() => { if (!cancelled) setStatus("bullbear", "offline"); });
    };
    load();
    const id = setInterval(load, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // ── Market Stress Monitor — fetch on mount + every 5 min ──
  // ⚠ Same 300s cadence as the two indicators above, and for the same reason:
  // the backend caches this for 300s, so a faster poll re-serves one object and
  // a slower one lets the panel lag the z-scores it is reading.
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      setStatus("stress", "loading");
      backendFetch("https://data.alpaca.markets", "/api/sentiment/stress")
        .then((r) => r.json())
        .then((data) => {
          if (cancelled) return;
          setStress(data && typeof data === "object" ? data : null);
          // ⚠ `calibrating` IS A LIVE READING, NOT AN OUTAGE. Marking it offline
          // would put a red status dot on a monitor that is working exactly as
          // designed and simply has not accumulated enough history yet.
          setStatus("stress", data && data.status && data.status !== "unavailable"
            ? "live" : "offline");
        })
        .catch(() => { if (!cancelled) setStatus("stress", "offline"); });
    };
    load();
    const id = setInterval(load, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // ── Put/Call ratio history from FRED ──
  useEffect(() => {
    if (!keys.fredKey && !window.QUANT_SIGNAL_BACKEND) return;
    // FRED series PCALL — CBOE Equity Put/Call ratio (if available)
    // Fallback: use VIX as a proxy for historical context
    backendFetch(`${FRED_BASE}?series_id=VIXCLS&api_key=${keys.fredKey}&file_type=json&limit=60&sort_order=desc`, `/api/fred/VIXCLS?limit=60`)
      .then((r) => r.json())
      .then((data) => {
        if (data.observations) {
          const obs = data.observations.filter((o) => o.value !== ".").reverse();
          setPcHistory(obs.map((o) => ({ date: o.date.slice(5), vix: parseFloat(o.value) })));
        }
      })
      .catch(() => {});
  }, [keys.fredKey]);

  const sentimentColor = (value, type = "standard") => {
    // standard: green=low, red=high (greed indexes)
    // inverted: green=high bullish (bull/bear surveys)
    if (type === "standard") {
      return value >= 75 ? C.red : value >= 55 ? C.orange : value >= 45 ? C.amber : value >= 25 ? C.teal : C.green;
    }
    return value >= 50 ? C.red : value <= 25 ? C.green : C.amber;
  };

  return (
    <div>
      {/* QS Fear/Greed Index — the sole sentiment display on this page */}
      <QSFearGreedPanel qsfg={qsfg} status={statusMap.qsfg} keys={keys} />

      {/* QS Bull/Bear — proprietary positioning composite. Sits directly under
          Fear/Greed deliberately: they answer adjacent questions ("how
          frightened" vs "how crowded") and are most useful read together. */}
      <QSBullBearPanel bullbear={bullbear} status={statusMap.bullbear} keys={keys} />

      {/* Market Stress Monitor — sits below both gauges. They report a LEVEL
          (how frightened, how crowded); this reports a STATE, and it is the
          one that tells you to act. */}
      <MarketStressPanel stress={stress} status={statusMap.stress} />

      {/* Crypto Fear & Greed — supplementary */}
      <div style={{ ...s.panel, marginBottom: 10 }}>
          <div style={s.panelH}>
            <span style={s.panelT}>CRYPTO FEAR & GREED</span>
            <DataStatus status={statusMap.cryptofg || "offline"} source="Crypto Fear & Greed · Daily" />
          </div>
          <div style={s.panelB}>
            <div style={{ fontSize: 13, color: C.textDim, marginBottom: 8, lineHeight: 1.6 }}>
              Crypto-specific: volatility, market momentum/volume, social media, dominance, trends. Contrarian signal at extremes.
            </div>
            {cryptoFG ? (
              <>
                <div style={{ fontSize: 32, fontWeight: 700, color: sentimentColor(cryptoFG.value), lineHeight: 1 }}>
                  {cryptoFG.value}
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: sentimentColor(cryptoFG.value), marginTop: 2, marginBottom: 10 }}>
                  {cryptoFG.label}
                  {cryptoFG.value >= 75 && " — Historically precedes correction"}
                  {cryptoFG.value <= 25 && " — Historically precedes recovery"}
                </div>
                <div style={{ height: 5, background: C.border, borderRadius: 2, overflow: "hidden" }}>
                  <div style={{ width: `${cryptoFG.value}%`, height: "100%", background: sentimentColor(cryptoFG.value) }} />
                </div>
              </>
            ) : <div style={{ color: C.textDim, fontSize: 13 }}>{statusMap.cryptofg === "offline" ? "Unavailable" : "Loading..."}</div>}
          </div>
        </div>

      {/* VIX history as sentiment context */}
      {pcHistory.length > 0 && (
        <div style={s.panel}>
          <div style={s.panelH}>
            <span style={s.panelT}>VIX HISTORY — SENTIMENT CONTEXT</span>
            <DataStatus status="delayed" source="" />
          </div>
          <div style={s.panelB}>
            <div style={{ fontSize: 13, color: C.textDim, marginBottom: 6 }}>
              VIX above 30 = fear regime. Below 12 = complacency. Both extremes are contrarian signals.
              Current sentiment gauges above should be read in the context of the VIX regime.
            </div>
            <ResponsiveContainer width="100%" height={130}>
              <AreaChart data={pcHistory} margin={{ top: 4, right: 4, left: 0, bottom: 4 }}>
                <defs>
                  <linearGradient id="vixGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor={C.red} stopOpacity={0.15} />
                    <stop offset="95%" stopColor={C.red} stopOpacity={0}    />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: C.textDim }} tickLine={false}
                  interval={Math.floor(pcHistory.length / 6)} />
                <YAxis tick={{ fontSize: 10, fill: C.textDim }} tickLine={false} axisLine={false}
                  domain={[0, "auto"]} width={28} />
                <Tooltip contentStyle={{ background: C.panel, border: `1px solid ${C.border}`, fontSize: 11 }}
                  formatter={(v) => [fmt(v), "VIX"]} />
                <ReferenceLine y={12} stroke={C.green + "55"} strokeDasharray="3 2" label={{ value: "12", fill: C.green, fontSize: 10 }} />
                <ReferenceLine y={20} stroke={C.amber + "55"} strokeDasharray="3 2" label={{ value: "20", fill: C.amber, fontSize: 10 }} />
                <ReferenceLine y={30} stroke={C.red   + "55"} strokeDasharray="3 2" label={{ value: "30", fill: C.red,   fontSize: 10 }} />
                <Area type="monotone" dataKey="vix" stroke={C.red} strokeWidth={1.5} fill="url(#vixGrad)" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div style={{ fontSize: 11, color: C.textDim, marginTop: 8, lineHeight: 1.6 }}>
        QS Fear/Greed Index · QS Bull/Bear Indicator · Market Stress Monitor · Crypto Fear & Greed ·
        All sentiment indicators are contrarian by nature — extremes signal positioning crowding, not price direction. ·{" "}
        <span style={{ color: "#FFD700", fontWeight: 700 }}>Not a recommendation.</span>
      </div>
    </div>
  );
}
